import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";

/**
 * Process revision script assets, and the two git verbs that publish them.
 *
 * These types are declared here rather than in `common` on purpose: nothing outside this feature
 * consumes them yet, and adding a shared type forces a frontend rebuild gate for no benefit.
 */

export type ScriptAsset = {
  id: number;
  path: string;
  contents: string;
  executable: boolean;
  contentHash: string;
};

export type ProcessExportResult = {
  branch: string;
  contentHash: string;
  files: string[];
  committed: boolean;
  revId: number;
  tag?: string;
};

export type ProcessGitConnection = {
  id: number;
  repoUrl: string;
  repoPath: string;
  defaultBranch: string;
  provider: "github" | "gitlab" | "bitbucket";
  serviceTokenId: number | null;
};

export type PublishedRevision = {
  revId: number;
  tag: string;
  /** A live, not deleted, revision row exists here with this id. */
  presentLocally: boolean;
};

export type PublishedRevisions = {
  repoPath: string;
  branch: string;
  highestExported: number;
  highestLocal: number | null;
  /** True when export will refuse, because the repository is ahead of everything held here. */
  repositoryIsAhead: boolean;
  revisions: PublishedRevision[];
};

/**
 * One published revision this installation never held. Mirrors `SyncPlan["importable"][number]`
 * in `backend/src/business/process/git/sync-plan.ts`.
 */
export type SyncImportableEntry = {
  revId: number;
  tag: string;
  revisionUuid: string;
  commitId: string;
};

/**
 * `.../git/sync/check`'s own importable entry: the same fields, plus the commit
 * `readAndValidateRevision` actually read and hashed for this candidate on the server. This, not
 * the plain `commitId` above, is the value an apply must echo back in `expectedCommitIds`, and the
 * value `consentText` names. Null when this candidate failed to validate; its reason then appears
 * in `importFailures` instead. Mirrors `SyncCheckImportableEntry` in
 * `backend/src/business/process/git/service.ts`.
 */
export type SyncCheckImportableEntry = SyncImportableEntry & {
  validatedCommitId: string | null;
};

/**
 * Mirrors `SyncImportFailureCode` in `backend/src/business/process/git/service.ts`. `unconfirmed`,
 * `commit-moved` and `stale-consent` are all fixed by running the check again and consenting
 * fresh. `invalid` and `conflict` are not: the source repository or the underlying data conflict
 * has to change first, so checking again alone will not help either one.
 */
export type SyncImportFailureCode =
  | "unconfirmed"
  | "commit-moved"
  | "invalid"
  | "conflict"
  | "stale-consent";

/** One tag named in a sync call that did not end up written, and why. */
export type SyncImportFailure = {
  code: SyncImportFailureCode;
  tag: string;
  revId: number | null;
  reason: string;
};

/**
 * Mirrors `ProcessSyncResult` in `backend/src/business/process/git/service.ts`: what a
 * `.../git/sync` call actually did, whether it only restored or also imported.
 */
export type ProcessSyncResult = {
  restored: number;
  imported: number;
  restore: { processId: number; revId: number; tag: string }[];
  alreadyPresent: { revId: number; tag: string }[];
  importable: SyncImportableEntry[];
  unresolved: { revId: number; tag: string; reason: string }[];
  importFailures: SyncImportFailure[];
  /** Always null on this response; see `ProcessSyncCheckReport.consentText` for the same prose while it is still actionable. */
  consentText: null;
};

/**
 * Mirrors `ProcessSyncCheckReport` in `backend/src/business/process/git/service.ts`: the read
 * only preview of what a sync would do, plus the Spike C consent text naming the repository and
 * every commit pending import. `consentText` is null exactly when nothing in `importable`
 * validated cleanly, which is also exactly when no consent step is needed at all.
 */
export type ProcessSyncCheckReport = {
  restore: { processId: number; revId: number; tag: string }[];
  alreadyPresent: { revId: number; tag: string }[];
  importable: SyncCheckImportableEntry[];
  unresolved: { revId: number; tag: string; reason: string }[];
  importFailures: SyncImportFailure[];
  consentText: string | null;
};

/** Matches `processGitSyncBodySchema` in `backend/src/routes/process.ts`. */
export type SyncProcessRevisionsBody = {
  /** Consent to importing the revisions named in `expectedCommitIds`. Omit to restore only. */
  confirmImport?: true;
  /** The commit id a prior check reported for each tag being imported, keyed by tag. */
  expectedCommitIds?: Record<string, string>;
};

export type ConnectRepoBody = {
  repoUrl: string;
  provider: string;
  defaultBranch: string;
  serviceTokenId: number | null;
};

export type PutScriptAssetBody = {
  path: string;
  contents: string;
  executable?: boolean;
};

const scriptAssetsKey = (processId: number): (string | number)[] => [
  "/api/v1/process",
  processId,
  "script-assets",
];

const gitConnectionKey = (processId: number): (string | number)[] => [
  "/api/v1/process",
  processId,
  "git",
  "connection",
];

const publishedRevisionsKey = (processId: number): (string | number)[] => [
  "/api/v1/process",
  processId,
  "git",
  "published",
];

export const FROZEN_REVISION_MESSAGE =
  "This revision is published, so its files are frozen. Create a new revision to change them.";

/**
 * A published revision is frozen, so the server refuses a content change with 409.
 *
 * That is not a generic failure and must not read as one: it is the publication barrier working,
 * and it is the single behaviour the revision lifecycle exists to guarantee.
 */
export const isPublishedRevisionConflict = (error: unknown): boolean =>
  409 === (error as { response?: { status?: number } })?.response?.status;

const detailOf = (error: ErrorNotification, fallback: string): string =>
  isPublishedRevisionConflict(error)
    ? FROZEN_REVISION_MESSAGE
    : (error?.response?.data?.message ?? fallback);

export const useScriptAssets = (
  processId: number | undefined
): UseQueryResult<ScriptAsset[], Error> =>
  useQuery<ScriptAsset[], Error>({
    queryKey: scriptAssetsKey(processId ?? 0),
    enabled: undefined !== processId && 0 < processId,
    queryFn: async () => {
      const res = await axios.get(`/api/v1/process/${processId}/script-assets`);
      return res.data as ScriptAsset[];
    },
  });

export const usePutScriptAsset = (
  processId: number
): UseMutationResult<ScriptAsset, ErrorNotification, PutScriptAssetBody> => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation<ScriptAsset, ErrorNotification, PutScriptAssetBody>({
    mutationFn: async body => {
      const res = await axios.put(
        `/api/v1/process/${processId}/script-assets`,
        body
      );
      return res.data as ScriptAsset;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: scriptAssetsKey(processId),
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "script-asset-save-error",
        message: isPublishedRevisionConflict(error)
          ? "Revision is published"
          : "Failed to save file",
        details: detailOf(error, "The file could not be saved."),
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteScriptAsset = (
  processId: number
): UseMutationResult<
  { deleted: boolean },
  ErrorNotification,
  { path: string }
> => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation<{ deleted: boolean }, ErrorNotification, { path: string }>(
    {
      mutationFn: async body => {
        const res = await axios.delete(
          `/api/v1/process/${processId}/script-assets`,
          { data: body }
        );
        return res.data as { deleted: boolean };
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: scriptAssetsKey(processId),
        });
      },
      onError: (error: ErrorNotification) => {
        setNotification({
          id: "script-asset-delete-error",
          message: isPublishedRevisionConflict(error)
            ? "Revision is published"
            : "Failed to remove file",
          details: detailOf(error, "The file could not be removed."),
          variant: NOTIFICATION.ERROR,
        });
      },
    }
  );
};

/**
 * The repository this process publishes to, or null if it has none yet.
 *
 * Null is a normal state, not an error: an unconnected process is where every process starts.
 * The panel renders its empty form from null and its connected form from a value, so the two
 * cases are one render path rather than two.
 */
export const useProcessGitConnection = (
  processId: number | undefined
): UseQueryResult<ProcessGitConnection | null, Error> =>
  useQuery<ProcessGitConnection | null, Error>({
    queryKey: gitConnectionKey(processId ?? 0),
    enabled: undefined !== processId && 0 < processId,
    queryFn: async () => {
      const res = await axios.get(
        `/api/v1/process/${processId}/git/connection`
      );
      return (res.data ?? null) as ProcessGitConnection | null;
    },
  });

/**
 * What the repository already holds for this process, or null if it has no repository.
 *
 * This reaches the network on the server side (one `ls-remote`), so it is deliberately NOT
 * refetched on window focus. A user tabbing back to the editor should not trigger a round trip to
 * a git host.
 *
 * It exists because of a real report: a user deleted two published revisions, could not export,
 * and said "I don't see the revisions". The export guard reads the repository's tags and refuses
 * anything below the highest, which is correct, but nothing displayed those tags. This is the
 * display.
 */
export const useProcessPublishedRevisions = (
  processId: number | undefined
): UseQueryResult<PublishedRevisions | null, Error> =>
  useQuery<PublishedRevisions | null, Error>({
    queryKey: publishedRevisionsKey(processId ?? 0),
    enabled: undefined !== processId && 0 < processId,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await axios.get(`/api/v1/process/${processId}/git/published`);
      return (res.data ?? null) as PublishedRevisions | null;
    },
  });

/**
 * Previews a sync without writing anything: the same plan `useSyncProcessRevisions` would act on,
 * plus the Spike C consent text naming the repository and every commit pending import. The panel
 * calls this first and only calls `useSyncProcessRevisions` once the plan, and any consent it
 * requires, has been shown to a human.
 *
 * A mutation, not a query, matching `useProcessGitImportScan`'s own reasoning
 * (`process-git-import.api.tsx`): this reaches the network on the server side (an `ls-remote`,
 * plus a fetch and a parse for every importable candidate), so caching it under a query key would
 * serve a stale plan about a repository the server has not re-checked.
 */
export const useCheckProcessSync = (
  processId: number
): UseMutationResult<ProcessSyncCheckReport, ErrorNotification, void> => {
  const { setNotification } = useNotifications();

  return useMutation<ProcessSyncCheckReport, ErrorNotification, void>({
    mutationFn: async () => {
      const res = await axios.post(
        `/api/v1/process/${processId}/git/sync/check`
      );
      return res.data as ProcessSyncCheckReport;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "process-git-sync-check-error",
        message: "Check failed",
        details: detailOf(error, "The repository could not be checked."),
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

/**
 * Restores revisions the repository still publishes and this process hid, and, given consent,
 * imports what it cannot restore.
 *
 * The body is optional and, without `confirmImport`, restores only: this is the exact call the
 * Sync button always made before this hook gained a consent step, and it keeps working unchanged
 * so a caller that never checks first still gets the safe, no-consent restore. `confirmImport` and
 * `expectedCommitIds` are never invented here; the panel gets both from a prior
 * `useCheckProcessSync` call and passes them straight through, bound to the exact commit that call
 * reported for each tag.
 *
 * Invalidates two lists, because a restore changes both, and leaving either one stale is what a
 * user experiences as "sync did nothing":
 *
 * - the published-revisions panel, whose `presentLocally` flags and `repositoryIsAhead` warning
 *   are the direct answer to the button that was just pressed;
 * - `["processRevisions"]`, which feeds the revision dropdown in the process edit modal. A
 *   restored revision belongs back in that dropdown, and every other mutation that changes the
 *   revision set already invalidates it. Without this the dropdown serves cache until the modal
 *   is closed and reopened, which is exactly how this was reported.
 */
export const useSyncProcessRevisions = (
  processId: number
): UseMutationResult<
  ProcessSyncResult,
  ErrorNotification,
  SyncProcessRevisionsBody | void
> => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation<
    ProcessSyncResult,
    ErrorNotification,
    SyncProcessRevisionsBody | void
  >({
    mutationFn: async body => {
      const res = await axios.post(
        `/api/v1/process/${processId}/git/sync`,
        body
      );
      return res.data as ProcessSyncResult;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: publishedRevisionsKey(processId),
      });
      // `refetchType: "all"` matches useDeleteProcessRevision, the mutation that hid the revision
      // in the first place. The delete and the restore have to reach the same cached lists, and
      // the default refetches only the mounted ones.
      void queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "process-git-sync-error",
        message: "Sync failed",
        details: detailOf(
          error,
          "Check that the repository and its credential are correct."
        ),
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useConnectProcessRepo = (
  processId: number
): UseMutationResult<
  ProcessGitConnection,
  ErrorNotification,
  ConnectRepoBody
> => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation<ProcessGitConnection, ErrorNotification, ConnectRepoBody>({
    mutationFn: async body => {
      const res = await axios.post(
        `/api/v1/process/${processId}/git/connect`,
        body
      );
      return res.data as ProcessGitConnection;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: gitConnectionKey(processId),
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "process-git-connect-error",
        message: "Failed to connect repository",
        details: detailOf(
          error,
          "A process cannot be repointed at a different repository once one is registered."
        ),
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useExportProcessRevision = (
  processId: number
): UseMutationResult<ProcessExportResult, ErrorNotification, void> => {
  const { setNotification } = useNotifications();

  return useMutation<ProcessExportResult, ErrorNotification, void>({
    mutationFn: async () => {
      const res = await axios.post(`/api/v1/process/${processId}/git/export`);
      return res.data as ProcessExportResult;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "process-git-export-error",
        message: "Export failed",
        details: detailOf(
          error,
          "Check that the repository and its credential are correct."
        ),
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
